import { RISK_TARGET_TYPES, OCCUPANCY_LEVELS } from "@/types/arboricultural-inspection";
import type { InspectionFormState, InspectionFormAction } from "../useInspectionForm";
import { useCtaCatalogs } from "@/hooks";
import TriState from "./TriState";

interface Props {
  state: InspectionFormState;
  dispatch: React.Dispatch<InspectionFormAction>;
}

const labelCls = "text-xs font-medium text-foreground-500 mb-1 block";
const selectCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors appearance-none cursor-pointer";
const inputCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors";

const RISK_CLASSES = [
  { value: "BAIXO", label: "Baixo", color: "text-primary-600 bg-primary-50 border-primary-200" },
  { value: "MEDIO", label: "Médio", color: "text-warning-600 bg-warning-50 border-warning-200" },
  { value: "ALTO", label: "Alto", color: "text-accent-600 bg-accent-50 border-accent-200" },
  { value: "CRITICO", label: "Crítico", color: "text-danger-600 bg-danger-50 border-danger-200" },
];

const TARGET_LABELS: Record<string, string> = {
  PEDESTRIAN: "Pedestres", VEHICLE: "Veículos", RESIDENCE: "Residência",
  COMMERCIAL_BUILDING: "Edificação comercial", SCHOOL: "Escola",
  PUBLIC_FACILITY: "Equipamento público", ELECTRICAL_NETWORK: "Rede elétrica",
  ROAD: "Via pública", URBAN_FURNITURE: "Mobiliário urbano", OTHER: "Outro",
};

const OCCUPANCY_LABELS: Record<string, string> = {
  LOW: "Baixa", MEDIUM: "Média", HIGH: "Alta", VERY_HIGH: "Muito alta", UNKNOWN: "Desconhecida",
};

// Escala A0-A2 (roteiro interno de capacitação do FloraIQ). Os rótulos vêm do servidor.
const A_FALLBACK = [
  { code: "A0", name: "Nenhum alvo relevante", caution: "Não pode coexistir com alvos detalhados na mesma vistoria." },
  { code: "A1", name: "Baixa exposição", caution: "Registrar o que foi observado; alvos detalhados são opcionais." },
  { code: "A2", name: "Alvo relevante", caution: "Exige ao menos um alvo detalhado (tipo e ocupação)." },
];

export default function StepRisco({ state, dispatch }: Props) {
  const ra = state.riskAssessment;
  const catalogs = useCtaCatalogs();
  const aOptions = catalogs?.target_classes ?? A_FALLBACK;
  const selectedA = aOptions.find((o) => o.code === ra.targetClass) ?? null;
  const pOptions = catalogs?.priority_classes ?? [];
  const selectedP = pOptions.find((o) => o.code === ra.priorityClass) ?? null;
  const targetCount = state.riskTargets.length;
  const aProblem =
    ra.targetClass === "A2" && targetCount === 0
      ? "A2 exige ao menos um alvo detalhado. Adicione abaixo."
      : ra.targetClass === "A0" && targetCount > 0
        ? "A0 significa nenhum alvo relevante, mas há alvos detalhados. Remova-os ou reclassifique."
        : null;

  return (
    <div className="space-y-5">
      {/* Risk class */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2 mb-4">
          <i className="ri-alert-line text-danger-500"></i>
          Classificação de Risco
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {RISK_CLASSES.map((rc) => (
            <label
              key={rc.value}
              className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                ra.riskClass === rc.value ? rc.color : "border-background-200/70 hover:bg-background-50"
              }`}
            >
              <input
                type="radio"
                name="riskClass"
                value={rc.value}
                checked={ra.riskClass === rc.value}
                onChange={() => dispatch({ type: "SET_RISK", field: "riskClass", value: rc.value })}
                className="accent-primary-500"
              />
              <span className="text-sm font-medium">{rc.label}</span>
            </label>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className={labelCls}>Score de risco (0-100)</label>
            <input
              type="number"
              min="0"
              max="100"
              value={ra.riskScore ?? ""}
              onChange={(e) =>
                dispatch({ type: "SET_RISK", field: "riskScore", value: e.target.value ? parseInt(e.target.value) : null })
              }
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Fundamentação do risco</label>
            <textarea
              rows={2}
              value={(ra.notes as string) ?? ""}
              onChange={(e) => dispatch({ type: "SET_RISK", field: "notes", value: e.target.value })}
              placeholder="Método, evidências e limites (obrigatória em risco crítico, P0 e P1)."
              className={`${inputCls} resize-none`}
            />
          </div>
        </div>

        {/* Resumo de alvos atingidos (três estados) */}
        <div className="mt-3 pt-3 border-t border-background-200/50">
          <p className="text-xs text-foreground-500 mb-1">Alvos atingidos (resumo)</p>
          <div className="divide-y divide-background-100">
            {(["pedestrians", "vehicles", "buildings", "powerGrid"] as const).map((f) => (
              <TriState
                key={f}
                label={{ pedestrians: "Pedestres", vehicles: "Veículos", buildings: "Edificações", powerGrid: "Rede elétrica" }[f]}
                value={(ra as Record<string, boolean | null | undefined>)[f]}
                onChange={(v) => dispatch({ type: "SET_RISK", field: f, value: v })}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Prioridade operacional P0-P4 */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
          <i className="ri-timer-flash-line text-warning-500"></i>
          Prioridade operacional (P0–P4)
        </h4>
        <p className="text-xs text-foreground-500 mt-1 mb-3">
          Ordena o atendimento e define o prazo, contado da data da vistoria. Não é a classe de risco.
          {catalogs?.priority_homologation?.status === "EXEMPLO_PROVISORIO" && (
            <span className="ml-1 text-warning-700">Matriz provisória, ainda sujeita à aprovação da AMMA.</span>
          )}
        </p>
        {pOptions.length === 0 ? (
          <p className="text-xs text-foreground-400">Catálogo de prioridades indisponível.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" role="radiogroup" aria-label="Prioridade operacional">
            {pOptions.map((o) => {
              const selected = ra.priorityClass === o.code;
              return (
                <button
                  key={o.code}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => dispatch({ type: "SET_RISK", field: "priorityClass", value: selected ? null : o.code })}
                  className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${
                    selected ? "border-primary-400 bg-primary-50" : "border-background-200/70 hover:bg-background-50"
                  }`}
                >
                  <span className="block text-sm font-bold text-foreground-800">{o.code}</span>
                  <span className="block text-xs text-foreground-700 leading-snug">{o.name}</span>
                  <span className="block text-[11px] text-foreground-500 mt-0.5">{o.deadline_label}</span>
                </button>
              );
            })}
          </div>
        )}
        {selectedP && (
          <div className="mt-3 text-xs rounded-lg bg-background-50 border border-background-200/60 px-3 py-2 text-foreground-600 space-y-1">
            <p><strong className="text-foreground-800">Quando usar:</strong> {selectedP.triggers.join("; ")}.</p>
            <p><strong className="text-foreground-800">Primeira ação:</strong> {selectedP.first_action}</p>
            <p><strong className="text-foreground-800">Responsável:</strong> {selectedP.responsible}</p>
            {selectedP.urgency_handling && <p>{selectedP.urgency_handling}</p>}
            {selectedP.caution && <p className="text-warning-700">{selectedP.caution}</p>}
          </div>
        )}
      </div>

      {/* Exposição de alvos A0-A2 */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
          <i className="ri-user-location-line text-accent-500"></i>
          Exposição de alvos (A0–A2)
        </h4>
        <p className="text-xs text-foreground-500 mt-1 mb-3">
          Quem ou o que pode ser atingido. É distinto da classe de risco e da prioridade.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Exposição de alvos">
          {aOptions.map((o) => {
            const selected = ra.targetClass === o.code;
            return (
              <button
                key={o.code}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => dispatch({ type: "SET_RISK", field: "targetClass", value: selected ? null : o.code })}
                className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${
                  selected ? "border-primary-400 bg-primary-50" : "border-background-200/70 hover:bg-background-50"
                }`}
              >
                <span className="block text-sm font-bold text-foreground-800">{o.code}</span>
                <span className="block text-xs text-foreground-600 leading-snug">{o.name}</span>
              </button>
            );
          })}
        </div>
        {selectedA && (
          <p className="mt-3 text-xs rounded-lg bg-background-50 border border-background-200/60 px-3 py-2 text-foreground-600">
            <strong className="text-foreground-800">{selectedA.code}:</strong> {selectedA.caution}
          </p>
        )}
        {aProblem && (
          <p className="mt-2 text-xs rounded-lg bg-danger-50 border border-danger-200/60 px-3 py-2 text-danger-700 flex gap-1.5">
            <i className="ri-error-warning-line mt-0.5"></i>
            {aProblem}
          </p>
        )}
      </div>

      {/* Risk targets */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
            <i className="ri-focus-3-line text-accent-500"></i>
            Alvos de Risco Detalhados
          </h4>
          <button
            type="button"
            onClick={() =>
              dispatch({ type: "ADD_RISK_TARGET", target: { targetType: "PEDESTRIAN" } })
            }
            className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            <i className="ri-add-line"></i> Adicionar alvo
          </button>
        </div>

        {state.riskTargets.length === 0 ? (
          <p className="text-xs text-foreground-400">Nenhum alvo de risco detalhado registrado.</p>
        ) : (
          <div className="space-y-3">
            {state.riskTargets.map((t, idx) => (
              <div key={idx} className="p-3 bg-background-50 rounded-lg border border-background-200/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-foreground-600">Alvo #{idx + 1}</span>
                  <button
                    type="button"
                    onClick={() => dispatch({ type: "REMOVE_RISK_TARGET", index: idx })}
                    className="text-xs text-danger-500 hover:text-danger-600"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelCls}>Tipo de alvo</label>
                    <select
                      value={t.targetType}
                      onChange={(e) =>
                        dispatch({ type: "UPDATE_RISK_TARGET", index: idx, field: "targetType", value: e.target.value })
                      }
                      className={selectCls}
                    >
                      {RISK_TARGET_TYPES.map((tt) => (
                        <option key={tt} value={tt}>{TARGET_LABELS[tt] ?? tt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Ocupação</label>
                    <select
                      value={t.occupancy ?? ""}
                      onChange={(e) =>
                        dispatch({ type: "UPDATE_RISK_TARGET", index: idx, field: "occupancy", value: e.target.value || null })
                      }
                      className={selectCls}
                    >
                      <option value="">—</option>
                      {OCCUPANCY_LEVELS.map((o) => (
                        <option key={o} value={o}>{OCCUPANCY_LABELS[o] ?? o}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Notas</label>
                    <input
                      type="text"
                      value={t.notes ?? ""}
                      onChange={(e) =>
                        dispatch({ type: "UPDATE_RISK_TARGET", index: idx, field: "notes", value: e.target.value || null })
                      }
                      placeholder="Notas..."
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
